import { useCallback, useEffect, useState } from 'react';
import { Megaphone, Search, X } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useServerList } from '../../hooks/useServerList';
import { toast } from '../../lib/toast';

type Audience = 'all_clinics' | 'pods' | 'clinics' | 'staff';
const MESSAGE_MAX = 500;

interface Person { _id: string; email: string; profile?: { firstName?: string; lastName?: string; clinicName?: string } }
interface BannerRow {
  id: string;
  message: string;
  audience: Audience;
  amIds: Person[];
  clinicIds: Person[];
  status: 'active' | 'ended';
  createdBy?: Person;
  createdAt: string;
  endedAt?: string;
  reach?: number;
  dismissals: number;
}
interface Picked { id: string; label: string }

const AUDIENCES: { value: Audience; label: string; description: string }[] = [
  { value: 'all_clinics', label: 'All clinics', description: 'Every clinic account' },
  { value: 'pods', label: 'AM pods', description: 'Chosen account managers and their clinics' },
  { value: 'clinics', label: 'Specific clinics', description: 'Hand-picked clinic accounts' },
  { value: 'staff', label: 'AMs and supervisors', description: 'Every account manager and supervisor' }
];

const personName = (p: Person) => p.profile?.clinicName || [p.profile?.firstName, p.profile?.lastName].filter(Boolean).join(' ') || p.email;

function audienceSummary(b: BannerRow): string {
  if (b.audience === 'pods') return `Pods: ${b.amIds.map(personName).join(', ')}`;
  if (b.audience === 'clinics') return `Clinics: ${b.clinicIds.map(personName).join(', ')}`;
  return AUDIENCES.find(a => a.value === b.audience)?.label ?? b.audience;
}

/** Admin → Banners: post a message across the top of the app for clinics, pods or staff. */
export default function AdminBanners() {
  const [banners, setBanners] = useState<BannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BannerRow | null>(null);
  const [confirmEnd, setConfirmEnd] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: BannerRow[] }>('/admin/banners');
      if (res.success) setBanners(res.data);
    } catch {
      toast.error('Couldn’t load banners');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const end = async (id: string) => {
    try {
      await apiClient.post(`/admin/banners/${id}/end`, {});
      toast.success('Banner ended');
      setConfirmEnd(null);
      if (editing?.id === id) setEditing(null);
      load();
    } catch {
      toast.error('Couldn’t end the banner');
    }
  };

  const active = banners.filter(b => b.status === 'active');
  const ended = banners.filter(b => b.status === 'ended');

  return (
    <div className="space-y-6">
      {/* The layout header carries the page title. */}
      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
        A message across the top of every page. It stays up until you end it; each person can dismiss it for themselves.
      </p>

      <section className="rounded-xl border p-6 shadow-sm" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <h2 className="mb-4 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{editing ? 'Edit banner' : 'New banner'}</h2>
        <BannerForm key={editing?.id ?? 'new'} banner={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Live now</h2>
        {loading ? <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading…</p>
          : active.length === 0 ? <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No banners are up.</p>
          : active.map(b => (
            <div key={b.id} className="rounded-lg border p-4" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="min-w-0 flex-1 whitespace-pre-line break-words text-sm" style={{ color: 'var(--text-primary)' }}>{b.message}</p>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => { setEditing(b); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>Edit</button>
                  {confirmEnd === b.id ? <>
                    <button type="button" onClick={() => end(b.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700">End for everyone</button>
                    <button type="button" onClick={() => setConfirmEnd(null)} className="px-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>Keep</button>
                  </> : <button type="button" onClick={() => setConfirmEnd(b.id)}
                    className="rounded-lg border px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50" style={{ borderColor: 'var(--border-primary)' }}>End</button>}
                </div>
              </div>
              <p className="mt-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {audienceSummary(b)} · {b.reach ?? 0} account{b.reach === 1 ? '' : 's'} · {b.dismissals} dismissed · posted {new Date(b.createdAt).toLocaleDateString()}{b.createdBy ? ` by ${personName(b.createdBy)}` : ''}
              </p>
            </div>
          ))}
      </section>

      {ended.length > 0 && (
        <details className="rounded-lg border p-4" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
          <summary className="cursor-pointer text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Ended ({ended.length})</summary>
          <ul className="mt-3 divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {ended.map(b => (
              <li key={b.id} className="py-3">
                <p className="whitespace-pre-line break-words text-sm" style={{ color: 'var(--text-secondary)' }}>{b.message}</p>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  {audienceSummary(b)} · {b.dismissals} dismissed · {new Date(b.createdAt).toLocaleDateString()} – {b.endedAt ? new Date(b.endedAt).toLocaleDateString() : ''}
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function BannerForm({ banner, onSaved, onCancel }: { banner: BannerRow | null; onSaved: () => void; onCancel: () => void }) {
  const [message, setMessage] = useState(banner?.message ?? '');
  const [audience, setAudience] = useState<Audience>(banner?.audience ?? 'all_clinics');
  const [ams, setAms] = useState<Picked[]>(banner?.amIds.map(p => ({ id: p._id, label: personName(p) })) ?? []);
  const [clinics, setClinics] = useState<Picked[]>(banner?.clinicIds.map(p => ({ id: p._id, label: personName(p) })) ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = { message, audience, amIds: ams.map(a => a.id), clinicIds: clinics.map(c => c.id) };
    try {
      if (banner) await apiClient.put(`/admin/banners/${banner.id}`, body);
      else await apiClient.post('/admin/banners', body);
      toast.success(banner ? 'Banner updated' : 'Banner posted');
      if (!banner) { setMessage(''); setAudience('all_clinics'); setAms([]); setClinics([]); }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Couldn’t save the banner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5">
      <div>
        <label htmlFor="banner-message" className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Message</label>
        <textarea id="banner-message" value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={MESSAGE_MAX}
          placeholder="New: patients can now message your clinic from their portal. Replies go in Care inbox."
          className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)', color: 'var(--text-primary)' }} />
        <p className="mt-1 text-right text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{message.length}/{MESSAGE_MAX}</p>
        {message.trim() && (
          <div className="mt-2 flex items-start gap-3 rounded-lg px-4 py-2.5 text-sm" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}>
            <Megaphone aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="min-w-0 flex-1 whitespace-pre-line break-words">{message.trim()}</p>
            <span className="text-xs opacity-70">Preview</span>
          </div>
        )}
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Who sees it</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {AUDIENCES.map(option => (
            <label key={option.value} className={`flex cursor-pointer items-start rounded-lg border p-3 transition-colors ${audience === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input type="radio" name="banner-audience" value={option.value} checked={audience === option.value} onChange={() => setAudience(option.value)} className="mr-3 mt-0.5" />
              <span>
                <span className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{option.label}</span>
                <span className="block text-xs" style={{ color: 'var(--text-tertiary)' }}>{option.description}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {audience === 'pods' && <AmPicker picked={ams} onChange={setAms} />}
      {audience === 'clinics' && <ClinicPicker picked={clinics} onChange={setClinics} />}

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving || !message.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : banner ? 'Save changes' : 'Post banner'}
        </button>
        {banner && <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>Cancel</button>}
      </div>
    </form>
  );
}

function Chips({ picked, onRemove }: { picked: Picked[]; onRemove: (id: string) => void }) {
  if (!picked.length) return null;
  return <div className="mb-3 flex flex-wrap gap-2">
    {picked.map(p => <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800">
      {p.label}
      <button type="button" onClick={() => onRemove(p.id)} aria-label={`Remove ${p.label}`} className="hover:text-blue-600"><X className="h-3 w-3" /></button>
    </span>)}
  </div>;
}

function AmPicker({ picked, onChange }: { picked: Picked[]; onChange: (p: Picked[]) => void }) {
  // Active AMs, searched on the server a page at a time. (This used to call
  // GET /users, which doesn't exist, so the list was always empty.)
  const list = useServerList<Person>({
    fetchPage: ({ page, pageSize, search, signal }) =>
      apiClient.searchUsersPage({ role: 'distributor', status: 'active', page, pageSize, search, signal }),
  });
  const toggle = (am: Person) => onChange(picked.some(p => p.id === am._id) ? picked.filter(p => p.id !== am._id) : [...picked, { id: am._id, label: personName(am) }]);
  const more = list.total !== null ? list.total > list.items.length : list.hasMore;
  return <div>
    <label htmlFor="banner-am-search" className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Account managers</label>
    <Chips picked={picked} onRemove={id => onChange(picked.filter(p => p.id !== id))} />
    <div className="relative mb-2">
      <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
      <input id="banner-am-search" value={list.search} onChange={e => list.setSearch(e.target.value)} placeholder="Search account managers"
        className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)', color: 'var(--text-primary)' }} />
    </div>
    {list.loading && !list.items.length ? <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading account managers…</p> : (
      <div className="max-h-48 overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
        {list.items.map(am => <label key={am._id} className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-gray-50" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={picked.some(p => p.id === am._id)} onChange={() => toggle(am)} />
          <span>{personName(am)} <span style={{ color: 'var(--text-tertiary)' }}>{am.email}</span></span>
        </label>)}
        {!list.items.length && <p className="px-3 py-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
          {list.error || (list.search ? `No active account managers match “${list.search}”.` : 'No active account managers.')}
        </p>}
        {more && list.items.length > 0 && <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
          Showing {list.items.length}{list.total !== null ? ` of ${list.total}` : ''}. Type to narrow the search.
        </p>}
      </div>
    )}
  </div>;
}

interface DirectoryRow { id: string; name: string; email: string; clinicName?: string }

function ClinicPicker({ picked, onChange }: { picked: Picked[]; onChange: (p: Picked[]) => void }) {
  // Server search; useServerList debounces and drops out-of-order answers.
  const list = useServerList<DirectoryRow>({
    pageSize: 10,
    fetchPage: async ({ search, pageSize, signal }) => {
      if (search.length < 2) return { items: [], total: 0, hasMore: false };
      const res = await apiClient.get<{ success: boolean; data: { rows: DirectoryRow[]; total?: number } }>(
        `/users/directory?role=physician&limit=${pageSize}&q=${encodeURIComponent(search)}`, { signal });
      const items = res.success ? res.data.rows : [];
      const total = res.success && typeof res.data.total === 'number' ? res.data.total : null;
      return { items, total, hasMore: total !== null ? total > items.length : false };
    },
  });
  const q = list.search;
  const rows = q.trim().length >= 2 ? list.items : [];
  const add = (row: DirectoryRow) => {
    if (!picked.some(p => p.id === row.id)) onChange([...picked, { id: row.id, label: row.clinicName || row.name }]);
    list.setSearch('');
  };
  return <div>
    <label htmlFor="banner-clinic-search" className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Clinics</label>
    <Chips picked={picked} onRemove={id => onChange(picked.filter(p => p.id !== id))} />
    <div className="relative">
      <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
      <input id="banner-clinic-search" value={q} onChange={e => list.setSearch(e.target.value)} placeholder="Search by clinic, name or email"
        className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)', color: 'var(--text-primary)' }} />
    </div>
    {rows.length > 0 && <ul className="mt-1 max-h-56 overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)' }}>
      {rows.map(row => <li key={row.id}>
        <button type="button" onClick={() => add(row)} className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-gray-50">
          <span style={{ color: 'var(--text-primary)' }}>{row.clinicName || row.name}</span>
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{row.name} · {row.email}</span>
        </button>
      </li>)}
      {list.hasMore && <li className="px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
        Showing {rows.length}{list.total !== null ? ` of ${list.total}` : ''}. Type more to narrow the search.
      </li>}
    </ul>}
  </div>;
}
